import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { RootStackParamList } from '$tablet-app/src/app/components/PrimaryNavigator';
import { useRootStackNavigation } from '$tablet-app/src/app/navigation/hooks';
import RecipeFiltersDialog from '$tablet-app/src/app/recipes/components/RecipeFiltersDialog';
import RecipeSelectView from '$tablet-app/src/app/recipes/components/RecipeSelectScreenView';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { MealType } from '@akin-home/amplify';
import { filterHasValue, getUnique } from '@akin-home/arrays';
import { useGetMeal, useSwapMealRecipe } from '@akin-home/meals/react';
import { useListOptions } from '@akin-home/options/react';
import { MEAL_TYPE_OPTIONS, Recipe } from '@akin-home/recipes';
import { getRecipeFilterOptionsFromUserPreferences } from '@akin-home/recipes-front-end';
import { useListAllRecipeModels } from '@akin-home/recipes/react';
import { findByID } from '@akin-home/records';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { getGraphqlFilterFromRecipeSearchScreenFilters } from '../../filterGroups/functions';

type Props = NativeStackScreenProps<RootStackParamList, 'RecipeSelect'>;

/** This Screen Components combines the State, Navigation and UI for Recipe Selection Screen */
export const RecipeSelectionScreen = ({ navigation, route }: Props) => {
  const { meal_id, meal_type } = route.params;

  const [loaded, setLoaded] = useState(false);

  const { navigate, goBack } = useRootStackNavigation();

  const { data: options } = useListOptions();

  const allergies = useStateStore((state) => state.current_user?.allergies);

  const favourite_recipe_ids = useStateStore(
    (state) => state.current_user?.favourite_recipe_ids
  );

  const {
    search_input,
    selected_recipe,
    setSearchInput,
    selectRecipe,
    active_filter_ids,
    excluded_filter_ids,
    reset,
    filter_dialog_visible,
    openFilterDialog,
    toggleActiveFilter,
    clearSelectedRecipe,
    setActiveFilters,
    setExcludedFilters,
  } = useStateStore((state) => state.recipe_search);

  const [filter_text] = useDebounce(search_input, 500);

  const { item: meal } = useGetMeal({ id: meal_id }, { enabled: !!meal_id });

  const title = getMealScreenTitle(meal_type);

  const active_filters = useMemo(() => {
    const all_options = [...(options || []), ...MEAL_TYPE_OPTIONS];

    const active_options = active_filter_ids.map((id) =>
      findByID(all_options, id)
    );

    const excluded_options = excluded_filter_ids.map((id) => {
      const option = findByID(all_options, id);

      return (
        option && {
          ...option,
          excl: true,
        }
      );
    });

    return filterHasValue([...active_options, ...excluded_options]);
  }, [options, active_filter_ids, excluded_filter_ids]);

  const filter = useMemo(
    () =>
      getGraphqlFilterFromRecipeSearchScreenFilters({
        name: filter_text,
        dropDownOptions: active_filters,
      }),
    [filter_text, active_filters]
  );

  const { recipes, isLoading } = useListAllRecipeModels({
    params: {
      filter: {
        ...filter?.filter,
        inProgress: { ne: true },
      },
    },
    options: {
      enabled: loaded,
    },
  });

  const favourites = useMemo(
    () =>
      favourite_recipe_ids &&
      recipes.filter((recipe) => favourite_recipe_ids.includes(recipe.id)),
    [recipes, favourite_recipe_ids]
  );

  // Handler for updating a meal
  const updateMeal = useSwapMealRecipe();

  /** When a recipe is clicked, we should be taken to the recipe details screen */
  const onClickRecipe = useCallback(
    (recipe: Recipe) =>
      navigate('RecipeDetails', {
        recipe_id: recipe.id,
        meal_id,
      }),
    [navigate, meal_id]
  );

  const onConfirmSwap = useCallback(async () => {
    if (selected_recipe) {
      if (meal) {
        updateMeal.mutateAsync({
          meal,
          recipe: selected_recipe,
        });

        goBack();
      }
    }
  }, [
    meal,
    selected_recipe,
    updateMeal,
    // createMeal,
    goBack,
    // habitatID,
  ]);

  useEffect(() => {
    if (options?.length && !loaded) {
      const [include, exclude] = getRecipeFilterOptionsFromUserPreferences({
        options,
        preferences: { allergies: allergies as string[] },
      });

      const active_filters = [...active_filter_ids, ...include];

      if (meal_type && !active_filter_ids.includes(meal_type)) {
        active_filters.push(meal_type);
      }

      setActiveFilters(active_filters);

      exclude.length &&
        setExcludedFilters(getUnique([...excluded_filter_ids, ...exclude]));

      setLoaded(true);
    }

    return () => {
      reset();
    };
  }, [options, meal_type]);

  useEffect(() => {
    if (!isLoading) {
      sendBackgroundMessage(intent_labels.open_recipe_search_screen);
    }
  }, [isLoading]);

  return (
    <>
      {/** The main recipe selection view */}
      <RecipeSelectView
        title={title}
        isLoading={isLoading}
        recipes={recipes}
        favourite_recipes={favourites}
        onConfirm={selected_recipe ? onConfirmSwap : undefined}
        onClickRecipe={onClickRecipe}
        onSelectRecipe={selectRecipe}
        selectedRecipe={selected_recipe}
        onChangeFilterText={setSearchInput}
        onCancel={navigation.goBack}
        onPressShowFilters={openFilterDialog}
        selectedFilters={active_filters}
        onPressRemoveFilter={toggleActiveFilter}
        onCancelSelectedRecipe={clearSelectedRecipe}
      />

      {/* The recipe selection filters modal */}
      <RecipeFiltersDialog visible={filter_dialog_visible} />
    </>
  );
};

const getMealScreenTitle = (meal_type: MealType) => {
  if (!meal_type) {
    return 'Select a recipe';
  }

  if (meal_type) {
    return `Change ${meal_type}`;
  }

  return 'Select a recipe';
};
